import { useState, useEffect } from 'react'
import {
  getDialogueSteps,
  updateDialogueStep,
  DialogueStep,
  DialogueStepInsert,
} from '@/services/chatbot-config'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { useToast } from '@/hooks/use-toast'
import { Loader2, Pencil } from 'lucide-react'
import { StepDialog } from './StepDialog'

export const DialogueFlowTab = () => {
  const [steps, setSteps] = useState<DialogueStep[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedStep, setSelectedStep] = useState<DialogueStep | null>(null)
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const { toast } = useToast()

  const fetchSteps = async () => {
    setLoading(true)
    const { data, error } = await getDialogueSteps()
    if (error) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Failed to load steps',
      })
    } else {
      setSteps(data || [])
    }
    setLoading(false)
  }

  useEffect(() => {
    fetchSteps()
  }, [])

  const handleUpdate = async (values: Partial<DialogueStepInsert>) => {
    if (!selectedStep) return
    setIsSubmitting(true)
    try {
      await updateDialogueStep(selectedStep.id, values)
      toast({ title: 'Step updated' })
      setIsDialogOpen(false)
      fetchSteps()
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Failed to update step',
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  const openEdit = (step: DialogueStep) => {
    setSelectedStep(step)
    setIsDialogOpen(true)
  }

  if (loading) return <Loader2 className="h-8 w-8 animate-spin mx-auto mt-10" />

  return (
    <div className="space-y-4">
      <div className="border rounded-md">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[80px]">Order</TableHead>
              <TableHead>Step ID</TableHead>
              <TableHead>Message</TableHead>
              <TableHead>Options</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {steps.map((step) => (
              <TableRow key={step.id}>
                <TableCell>{step.sort_order}</TableCell>
                <TableCell className="font-mono text-xs font-medium bg-slate-50 p-2 rounded">
                  {step.step_identifier}
                </TableCell>
                <TableCell
                  className="max-w-[300px] truncate"
                  title={step.message_text}
                >
                  {step.message_text}
                </TableCell>
                <TableCell>
                  <div className="flex flex-wrap gap-1">
                    {step.options?.length > 0 ? (
                      step.options.map((opt, i) => (
                        <Badge key={i} variant="outline" className="text-xs">
                          {opt.label} → {opt.next_step || '(End)'}
                        </Badge>
                      ))
                    ) : (
                      <span className="text-xs text-muted-foreground">
                        Free text input
                      </span>
                    )}
                  </div>
                </TableCell>
                <TableCell className="text-right">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => openEdit(step)}
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <StepDialog
        open={isDialogOpen}
        onOpenChange={setIsDialogOpen}
        initialData={selectedStep}
        onSubmit={handleUpdate}
        isSubmitting={isSubmitting}
      />
    </div>
  )
}
